import React, { useEffect, useState } from "react";

import datalign_logo from "datalign-module-template/assets/datalign_logo.jpg";
import nt_logo from "datalign-module-template/assets/nt_logo.png";
import {
  Box,
  Button,
  CssBaseline,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  EasierTheme,
  EasierThemeProvider,
  Themes,
  TokenThemes,
} from "datalign-themes";
import { AppBar, EzrImage, ProfileMenu, SplashPage, Toaster } from "ui-widgets";
import background_image from "datalign-module-template/assets/blue-two-min-grey.png";
import nt_background_image from "datalign-module-template/assets/nt-search-background.png";
import RouteManager from "./components/common/route-manager";
import AuthService from "services/AuthService";
import { createSearchParams } from "react-router-dom";
import Footer from "components/sitelet/footer/Footer";

const { current_user } = new AuthService();
const toolbarHeight = ["FAF-nt", "nrdata-orig"].includes(
  process.env.REACT_APP_STYLING_THEME ?? "",
)
  ? 80
  : 48;
const App = () => {
  const [theme] = useState(
    createTheme(
      ["FAF-nt", "nrdata-orig"].includes(
        process.env.REACT_APP_STYLING_THEME ?? "",
      )
        ? { ...Themes.NTG_LIGHT }
        : { ...Themes.COMBASE_LIGHT },
    ),
  );

  const [easierTheme] = useState(
    new EasierTheme(
      TokenThemes.MakePalette(
        ["FAF-nt", "nrdata-orig"].includes(
          process.env.REACT_APP_STYLING_THEME ?? "",
        )
          ? { ...TokenThemes.NTG_LIGHT }
          : { ...TokenThemes.COMBASE_LIGHT },
      ),
      "light",
    ),
  );

  const [profileAnchor, setProfileAnchor] = useState<HTMLElement>();
  const [signedIn, setSignedIn] = useState<boolean>();

  useEffect(() => {
    current_user()
      .then((user) => {
        if (user) {
          AuthService.CurrentUser = user;
        } else {
          AuthService.CurrentUser = undefined;
        }
      })
      .catch((res) => {
        AuthService.CurrentUser = undefined;
        res?.statusCode !== 401 && Toaster.makeToast(res.message, "error");
      })
      .finally(() => {
        setSignedIn(true);
      });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <EasierThemeProvider theme={easierTheme}>
        <CssBaseline />
        <SplashPage
          open={!signedIn}
          message={"Checking session..."}
          backgroundColour={easierTheme.Colour.Primary()}
          logo={datalign_logo}
          messageColour={easierTheme.Colour.OnPrimary()}
        />
        {signedIn && (
          <Box
            sx={{
              height: toolbarHeight,
              maxWidth: "100vw",
            }}
          >
            <AppBar
              hideProfile={
                `https://${window.location.host}` !== process.env.REACT_APP_HOME
              }
              ToolBarProps={{
                sx: {
                  minHeight: toolbarHeight,
                  height: toolbarHeight,
                  width: "100vw",
                },
              }}
              logo={
                ["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? nt_logo
                  : datalign_logo
              }
              title={
                process.env.REACT_APP_SITE_TITLE
                  ? process.env.REACT_APP_SITELET_NAME
                  : "Sitelet"
              }
              LogoProps={{
                style: {
                  cursor: "pointer",
                  height: ["FAF-nt", "nrdata-orig"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? 80
                    : 48,
                  padding: 4,
                },
                onClick: () =>
                  (window.location.href =
                    process.env.REACT_APP_LOGO_LINK ??
                    process.env.REACT_APP_HOME ??
                    ""),
              }}
              DividerProps={
                ["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      variant: "middle",
                      color: "white",
                      sx: {
                        height: "80%",
                        borderWidth: "1px",
                        m: 1,
                        borderColor: "white",
                      },
                    }
                  : undefined
              }
              TitleProps={
                ["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      fontSize: "21px",
                      fontWeight: 600,
                      lineHeight: "1.7rem",
                      margin: 0,
                      flexWrap: "wrap",
                      p: 1,
                    }
                  : undefined
              }
              onAvatarClick={(e) => setProfileAnchor(e.currentTarget)}
              secondaryButtons={
                ["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                ) ? undefined : (
                  <Button
                    size={"small"}
                    style={{ color: "white" }}
                    onClick={() => (window.location.hash = "/explorer")}
                  >
                    Explorer
                  </Button>
                )
              }
            />
            <ProfileMenu
              anchor={profileAnchor}
              onClose={() => setProfileAnchor(undefined)}
              userName={AuthService.CurrentUser?.name}
              userEmail={AuthService.CurrentUser?.email}
              userImageBackground={easierTheme?.Colour?.SurfaceContainerLow()}
              onLogOut={() =>
                (window.location.href = `${process.env.REACT_APP_HOME}/logout-page`)
              }
              onLogIn={() =>
                (window.location.href = `${process.env.REACT_APP_LOGIN}?${createSearchParams({ redirect_url: process.env.REACT_APP_HOST ?? "" })}`)
              }
            />

            <Box
              sx={
                ["FAF-nt", "nrdata-orig"].includes(
                  process.env.REACT_APP_STYLING_THEME ?? "",
                )
                  ? {
                      marginTop: "80px",
                      width: "100%",
                      minHeight: `calc(100vh - 80px)`,
                    }
                  : {
                      marginTop: "48px",
                      width: "100%",
                      minHeight: "calc(100vh - 48px)",
                    }
              }
            >
              <EzrImage
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  overflow: "hidden",
                  zIndex: -1,
                  opacity: ["FAF-nt", "nrdata-orig"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? 0.3
                    : 1,
                }}
                src={
                  ["FAF-nt", "nrdata-orig"].includes(
                    process.env.REACT_APP_STYLING_THEME ?? "",
                  )
                    ? nt_background_image
                    : background_image
                }
              />
              <RouteManager signedIn={signedIn} />
            </Box>
            <Footer />
          </Box>
        )}
        <Toaster />
      </EasierThemeProvider>
    </ThemeProvider>
  );
};

export default App;
